import { Link } from 'react-router-dom';
import { ArrowLeft, Check } from 'lucide-react';

export function BillingInfo() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h1 className="text-2xl font-bold text-[#2563eb] mb-6">Prissättning och Betalningsinformation</h1>
        
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-[#A3C1DA]">Vår prisfilosofi</h2>
          <p className="mb-4">
            På SOKASTIFTELSEMEDEL.SE tror vi på att göra stiftelseinformation tillgänglig för alla. 
            Vår plattform erbjuder både kostnadsfria funktioner och premiumtjänster för att möta olika behov och budgetar.
          </p>
          <p>
            Vi förstår att många som söker stiftelsemedel gör det för att de har begränsade ekonomiska resurser. 
            Därför har vi utformat vår prismodell för att vara så inkluderande som möjligt, samtidigt som vi kan 
            täcka kostnaderna för att driva och utveckla vår avancerade plattform.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-[#A3C1DA]">Kostnadsfria funktioner</h2>
          <p className="mb-4">
            Vi är stolta över att kunna erbjuda flera kraftfulla funktioner helt kostnadsfritt:
          </p>
          
          <ul className="space-y-3 mb-4">
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Grundläggande sökning</strong> - Sök bland över 17 000 svenska stiftelser baserat på grundläggande kriterier
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Stiftelseinformation</strong> - Tillgång till grundläggande information om stiftelser, inklusive ändamål och kontaktuppgifter
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Kunskapsresurser</strong> - Tillgång till vår omfattande informationssida om stiftelser och ansökningsprocesser
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Kommande gratistjänster</strong> - Vi arbetar kontinuerligt på att utöka våra kostnadsfria funktioner för att hjälpa fler människor
              </span>
            </li>
          </ul>
          
          <p>
            Vårt mål är att dessa kostnadsfria funktioner ska vara tillräckliga för många användare, 
            särskilt privatpersoner med begränsade resurser som söker stipendier eller bidrag.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-[#A3C1DA]">Premiumtjänster</h2>
          <p className="mb-4">
            För användare med mer specifika behov eller som vill spara tid erbjuder vi premiumtjänster 
            som ger tillgång till avancerade funktioner:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-lg mb-2">Avancerad sökning</h3>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>AI-driven matchning mot dina specifika behov</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Filtrering på flera parametrar samtidigt</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Detaljerad information om utdelningshistorik</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600">
                Spara timmar av manuell sökning genom vår intelligenta matchningsalgoritm.
              </p>
            </div>
            
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-lg mb-2">Djupanalys</h3>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Automatisk analys av stiftelsers stadgar</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Jämförelse av ansökningskrav och deadlines</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Insikter om framgångsfaktorer för ansökningar</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600">
                Få djupare förståelse för stiftelsernas prioriteringar och beslutsprocesser.
              </p>
            </div>
            
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-lg mb-2">Ansökningshjälp</h3>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Mallar för ansökningar anpassade till specifika stiftelser</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>AI-assisterad formulering av ansökningstexter</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Checklista för komplett ansökan</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600">
                Öka dina chanser att få bidrag genom professionellt utformade ansökningar.
              </p>
            </div>
            
            <div className="bg-[#F8F9FA] p-4 rounded-lg border border-gray-200">
              <h3 className="font-semibold text-lg mb-2">Organisationsverktyg</h3>
              <ul className="space-y-2 mb-3">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Spara och organisera relevanta stiftelser</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Påminnelser om ansökningsdeadlines</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
                  <span>Spårning av inskickade ansökningar</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600">
                Håll ordning på dina ansökningar och maximera dina chanser till finansiering.
              </p>
            </div>
          </div>
          
          <p>
            Dessa premiumtjänster är utformade för att spara veckor av research och förberedelser, 
            vilket gör dem till en värdefull investering för många användare.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-[#A3C1DA]">Varför vi tar betalt för vissa tjänster</h2>
          <p className="mb-4">
            Våra premiumtjänster drivs av avancerad AI-teknologi som kräver betydande resurser:
          </p>
          
          <ul className="space-y-3 mb-4">
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
              <span>
                <strong>AI-kostnader</strong> - Våra avancerade AI-arbetsflöden är tokenkrävande och kostar betydande summor att köra. 
                Varje djupanalys av stiftelsestadgar eller AI-assisterad ansökan kräver omfattande beräkningsresurser.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
              <span>
                <strong>Datainsamling och analys</strong> - Vi investerar kontinuerligt i att samla in, strukturera och 
                analysera data från tusentals stiftelser för att hålla vår information aktuell och korrekt.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-[#A3C1DA] mt-0.5 flex-shrink-0" />
              <span>
                <strong>Utveckling av specialverktyg</strong> - Våra skräddarsydda verktyg för webbskrapning, djupanalys 
                och matchning kräver kontinuerlig utveckling och förbättring.
              </span>
            </li>
          </ul>
          
          <p>
            Det är viktigt att notera att all information vi tillhandahåller är offentlig, men den är spridd över 
            internet utan något enkelt eller tidsbesparande sätt att hitta eller koppla samman den. Vårt värde ligger 
            i att samla, strukturera och analysera denna information för att göra den användbar och tillgänglig.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-[#A3C1DA]">Prisplaner</h2>
          
          <div className="grid md:grid-cols-4 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 p-4">
                <h3 className="font-semibold text-lg text-center">Gratis</h3>
                <p className="text-center text-2xl font-bold mt-2">0 kr</p>
                <p className="text-center text-sm text-gray-600">För alltid</p>
              </div>
              <div className="p-4">
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Grundläggande sökning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Stiftelseinformation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Kunskapsresurser</span>
                  </li>
                </ul>
                <Link
                  to="/#search"
                  className="block text-center bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition-colors"
                >
                  Börja söka
                </Link>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#E6F0FA] p-4 text-gray-800">
                <h3 className="font-semibold text-lg text-center">Dagspass</h3>
                <p className="text-center text-2xl font-bold mt-2">99 kr</p>
                <p className="text-center text-sm text-gray-600">för 24 timmar</p>
              </div>
              <div className="p-4">
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Allt i Gratis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Avancerad sökning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Djupanalys (3 st)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Organisationsverktyg</span>
                  </li>
                </ul>
                <Link
                  to="/premium"
                  className="block text-center bg-[#E6F0FA] text-gray-800 px-4 py-2 rounded hover:bg-[#D1E5F5] transition-colors"
                >
                  Köp dagspass
                </Link>
              </div>
            </div>
            
            <div className="border border-[#A3C1DA] rounded-lg overflow-hidden shadow-md relative">
              <div className="absolute top-0 right-0 bg-[#F2C1D1] text-white text-xs font-semibold px-2 py-1">
                Populärast
              </div>
              <div className="bg-[#A3C1DA] p-4 text-white">
                <h3 className="font-semibold text-lg text-center">Standard</h3>
                <p className="text-center text-2xl font-bold mt-2">299 kr</p>
                <p className="text-center text-sm text-white/80">per månad</p>
              </div>
              <div className="p-4">
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Allt i Gratis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Avancerad sökning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Djupanalys (10/månad)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Organisationsverktyg</span>
                  </li>
                </ul>
                <Link 
                  to="/premium" 
                  className="block text-center bg-[#A3C1DA] text-white px-4 py-2 rounded hover:bg-[#7A98B0] transition-colors"
                >
                  Uppgradera
                </Link>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-[#F2C1D1] p-4 text-white">
                <h3 className="font-semibold text-lg text-center">Premium</h3>
                <p className="text-center text-2xl font-bold mt-2">499 kr</p>
                <p className="text-center text-sm text-white/80">per månad</p>
              </div>
              <div className="p-4">
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Allt i Standard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Obegränsad djupanalys</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Ansökningshjälp</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Prioriterad support</span>
                  </li>
                </ul>
                <Link 
                  to="/premium" 
                  className="block text-center bg-[#F2C1D1] text-white px-4 py-2 rounded hover:bg-[#D1A3B3] transition-colors"
                >
                  Uppgradera
                </Link>
              </div>
            </div>
          </div>
          
          <p className="text-center text-sm text-gray-600 italic">
            Alla priser är inklusive moms. Prenumerationer kan avslutas när som helst.
          </p>
        </section>
        
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3 text-[#A3C1DA]">Vårt löfte till användare med begränsade resurser</h2>
          <p className="mb-4">
            Vi förstår att många som söker stiftelsemedel gör det just för att de har begränsade ekonomiska resurser. 
            Därför arbetar vi kontinuerligt på att:
          </p>
          
          <ul className="space-y-3 mb-4">
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Utöka våra kostnadsfria funktioner</strong> - Vi utvecklar ständigt nya gratistjänster för att hjälpa fler människor.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Erbjuda rabatter</strong> - För studenter, ideella organisationer och andra grupper med begränsade resurser.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Skapa informationsresurser</strong> - Som hjälper användare att effektivt söka stiftelsemedel även utan premiumfunktioner.
              </span>
            </li>
          </ul>
          
          <p>
            Vårt mål är att demokratisera tillgången till stiftelsemedel genom att göra information och verktyg 
            tillgängliga för alla, oavsett ekonomiska förutsättningar.
          </p>
        </section>
        
        <div className="bg-[#F2F7FF] p-4 rounded-lg border border-[#A3C1DA] mb-8">
          <h2 className="text-lg font-semibold mb-2 text-[#2563eb]">Har du frågor om våra tjänster eller priser?</h2>
          <p className="mb-3">
            Kontakta oss gärna för mer information eller för att diskutera speciallösningar för din organisation.
          </p>
          <Link to="/#contact" className="inline-block bg-[#A3C1DA] text-white px-4 py-2 rounded hover:bg-[#7A98B0] transition-colors">
            Kontakta oss
          </Link>
        </div>
        
        <div className="mt-8 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">Senast uppdaterad: 1 april 2025</p>
          <div className="mt-4">
            <Link to="/" className="text-[#2563eb] hover:underline flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Tillbaka till startsidan
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}